import React, { useCallback, useEffect, useState } from 'react';
import { FaCar, FaIdCard, FaMobileAlt, FaUserCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { userService } from '../../services/userService';
import { User, Vehicle } from '../../types';

interface DriverCardSupportProps {
  user: User;
  /** Recarrega a lista depois de uma alteração */
  onChanged: () => void;
}

const formatDateTime = (value?: string) =>
  value ? new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '';

/**
 * Suporte ao cartão do motorista: o motorista não troca sozinho aparelho, foto nem placa
 */
const DriverCardSupport: React.FC<DriverCardSupportProps> = ({ user, onChanged }) => {
  const [deviceBound, setDeviceBound] = useState(!!user.aparelhoVinculado);
  const [hasPhoto, setHasPhoto] = useState(!!user.temFoto);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [busy, setBusy] = useState(false);

  const loadVehicles = useCallback(async () => {
    const response = await userService.getVehicles(user.id);
    setVehicles(response.success && response.data ? response.data : []);
  }, [user.id]);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  useEffect(() => {
    if (!hasPhoto) {
      setPhotoUrl(null);
      return;
    }
    let url: string | null = null;
    userService.getPhotoUrl(user.id).then((result) => {
      url = result;
      setPhotoUrl(result);
    });
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [user.id, hasPhoto]);

  const run = async (
    confirmMessage: string,
    action: () => Promise<{ success: boolean; message?: string }>,
    onSuccess: () => void
  ) => {
    if (!window.confirm(confirmMessage)) return;
    setBusy(true);
    try {
      const response = await action();
      if (response.success) {
        toast.success(response.message || 'Feito');
        onSuccess();
        onChanged();
      } else {
        toast.error(response.message || 'Não foi possível concluir');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleReleaseDevice = () =>
    run(
      `Desvincular o aparelho de ${user.fullName}? O app no aparelho atual sai da conta e o motorista poderá entrar em outro celular.`,
      () => userService.releaseDevice(user.id),
      () => setDeviceBound(false)
    );

  const handleDeletePhoto = () =>
    run(
      `Remover a foto do cartão de ${user.fullName}? O motorista precisará tirar outra pelo app.`,
      () => userService.deletePhoto(user.id),
      () => setHasPhoto(false)
    );

  const handleDeleteVehicle = (vehicle: Vehicle) =>
    run(
      `Remover o veículo ${vehicle.placa} de ${user.fullName}? O motorista poderá cadastrar outra placa.`,
      () => userService.deleteVehicle(vehicle.id),
      loadVehicles
    );

  return (
    <div className="driver-card-support">
      <h3>
        <FaIdCard /> Cartão do motorista
      </h3>

      <div className="support-row">
        <div className="support-info">
          <FaMobileAlt className="support-icon" />
          <div>
            <strong>Aparelho</strong>
            <span>
              {deviceBound
                ? `Vinculado${user.aparelhoVinculadoEm ? ` desde ${formatDateTime(user.aparelhoVinculadoEm)}` : ''}`
                : 'Nenhum aparelho vinculado'}
            </span>
          </div>
        </div>
        {deviceBound && (
          <button type="button" className="btn-support" onClick={handleReleaseDevice} disabled={busy}>
            Desvincular aparelho
          </button>
        )}
      </div>

      <div className="support-row">
        <div className="support-info">
          {photoUrl ? (
            <img src={photoUrl} alt={`Foto de ${user.fullName}`} className="support-photo" />
          ) : (
            <FaUserCircle className="support-icon" />
          )}
          <div>
            <strong>Foto</strong>
            <span>{hasPhoto ? 'Cadastrada' : 'Sem foto'}</span>
          </div>
        </div>
        {hasPhoto && (
          <button type="button" className="btn-support" onClick={handleDeletePhoto} disabled={busy}>
            Remover foto
          </button>
        )}
      </div>

      {vehicles.length === 0 ? (
        <div className="support-row">
          <div className="support-info">
            <FaCar className="support-icon" />
            <div>
              <strong>Placa</strong>
              <span>Nenhum veículo cadastrado</span>
            </div>
          </div>
        </div>
      ) : (
        vehicles.map((vehicle) => (
          <div className="support-row" key={vehicle.id}>
            <div className="support-info">
              <FaCar className="support-icon" />
              <div>
                <strong>Placa {vehicle.placa}</strong>
                <span>{[vehicle.marca, vehicle.modelo, vehicle.cor].filter(Boolean).join(' ') || '-'}</span>
              </div>
            </div>
            <button
              type="button"
              className="btn-support"
              onClick={() => handleDeleteVehicle(vehicle)}
              disabled={busy}
            >
              Remover veículo
            </button>
          </div>
        ))
      )}
    </div>
  );
};

export default DriverCardSupport;
